import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import '@splidejs/react-splide/css';
import { fetchAllBrowse } from '../../slices/productSlice';
import { ProductGridSkeleton } from '../../components/ProductCardSkeleton';
import { usePageContent } from '../../hooks/usePageContent';
import { executeQuery, executeMutation } from '../../graphql';
import { plateImageGenerator, combinationRecommender } from '../../graphql/mutations';
import { getVariantWithColourThemeAndPlatesByProductId, validatePlateNumber } from '../../graphql/queries';
import { preparePlateImageInput, getPlateImageDataUrl } from '../../utils/plateImageHelper';
import styles from './index.module.css';
import ErrorPage from '../ErrorPage';

const formatPrice = (price) => (!price ? '0' : price.toLocaleString());

const evaluateRule = (rule, input) => {
  const upper = input.toUpperCase();
  if (!upper.length) return rule.type === 'notStartWith' || rule.type === 'notRegex';
  switch (rule.type) {
    case 'exactLength': return upper.length === rule.count;
    case 'rangeLength': return upper.length >= rule.min && upper.length <= rule.max;
    case 'regex': return new RegExp(rule.pattern, 'i').test(upper);
    case 'notStartWith': return !upper.startsWith((rule.prefix || '').toUpperCase());
    case 'notRegex': return !(new RegExp(rule.pattern, 'i').test(upper));
    default: return true;
  }
};

const BrowseStyleDetailPage = () => {
  const { style } = useParams();
  const dispatch = useDispatch();
  const { products, loading } = useSelector((state) => state.product);

  const { find: findLabels } = usePageContent('browse-styles-detail');
  const c = findLabels('styleDetail') || {};

  const { find: findPageData, loaded: pageLoaded } = usePageContent(`browse-styles/${style}`);
  const pageData       = findPageData('pageData')          || {};
  const galleryContent = findPageData('imageGallery')      || {};
  const plateSpecs     = findPageData('plateSpecs')        || {};
  const additionalInfo = findPageData('additionalDetails') || {};
  const overallDisclm  = findPageData('overallDisclaimer') || {};
  const buyNowModal    = findPageData('buyNowModal')        || {};
  const secondaryProduct = findPageData('secondaryProduct') || null;

  const galleryItems = galleryContent.items || [];

  const [openSections, setOpenSections] = useState({ furtherDetails: false, additionalInfo: false, combinations: false, secondaryDetails: false, secondarySpecs: false, secondaryCombinations: false, secondaryGoodToKnow: false });
  const [coloursExpanded, setColoursExpanded] = useState(false);
  const [buyNowModalOpen, setBuyNowModalOpen] = useState(false);
  const [selectedProductIdx, setSelectedProductIdx] = useState(0);
  const [comboInput, setComboInput] = useState('');
  const [generatedPreviewImage, setGeneratedPreviewImage] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState('');
  const [isAvailable, setIsAvailable] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [recommendedCombos, setRecommendedCombos] = useState([]);
  const [recommendationImages, setRecommendationImages] = useState([]);
  const [recsLoading, setRecsLoading] = useState(false);
  const buyNowDialogRef = useRef(null);
  const COLOURS_VISIBLE = 6;

  const navigate = useNavigate();

  useEffect(() => {
    if (buyNowModalOpen && buyNowDialogRef.current && !buyNowDialogRef.current.open) {
      buyNowDialogRef.current.showModal();
      // Avoid native <dialog> auto-focusing the close/back button (see
      // RestyleStylesPage) — focus the modal title instead.
      buyNowDialogRef.current
        .querySelector('.modal-selection__title, .modal-product-combo__title')
        ?.focus();
      setSelectedProductIdx(0);
      setComboInput('');
    }
  }, [buyNowModalOpen]);

  useEffect(() => {
    setComboInput('');
    setGeneratedPreviewImage(null);
    setAvailabilityError('');
    setIsAvailable(null);
    setSelectedVariant(null);
    setRecommendedCombos([]);
    setRecommendationImages([]);
  }, [selectedProductIdx]);

  useEffect(() => {
    if (!buyNowModalOpen) return;
    const currentSelProd = buyNowModal.products?.[selectedProductIdx] || null;
    if (!currentSelProd) return;

    const rules = currentSelProd.rules || [];
    const altRulesForPreview = rules.filter(r => r.isAlternative);
    const nonAltRulesForPreview = rules.filter(r => !r.isAlternative);
    const allPass = comboInput.length > 0 &&
      nonAltRulesForPreview.every(r => evaluateRule(r, comboInput)) &&
      (altRulesForPreview.length === 0 || altRulesForPreview.some(r => evaluateRule(r, comboInput)));

    setAvailabilityError('');
    setIsAvailable(null);
    if (!allPass) {
      setGeneratedPreviewImage(null);
      return;
    }

    let cancelled = false;
    const fetchImage = async () => {
      try {
        const targetProduct = products?.find(
          p => p.ProductName === currentSelProd.productName
        );
        if (!targetProduct) return;

        const fullCombo = (currentSelProd.comboPrefix || '') + comboInput;

        const variantsResult = await executeQuery(
          getVariantWithColourThemeAndPlatesByProductId,
          { ProductId: targetProduct.ProductId, CombinationType: fullCombo, SalesChannel: 1, IsDisplayBrowse: false }
        );
        if (cancelled) return;

        const variants = variantsResult?.getVariantByProductId;
        if (!variants?.length) return;

        const colourName = currentSelProd.previewColourDisplayName;
        const variant = (colourName
          ? variants.find(v => v.ColourInfo?.DisplayName === colourName && v.SizeInfo?.SizeCode === 'ST')
            || variants.find(v => v.ColourInfo?.DisplayName === colourName)
          : null)
          || variants.find(v => v.SizeInfo?.SizeCode === 'ST')
          || variants[0];
        if (!cancelled) setSelectedVariant(variant);
        const input = preparePlateImageInput(variant, fullCombo);
        if (!input) return;

        const result = await executeMutation(plateImageGenerator, { input });
        if (!cancelled && result?.generatePlateImage) {
          setGeneratedPreviewImage(getPlateImageDataUrl(result.generatePlateImage));
        }
      } catch {
        if (!cancelled) setGeneratedPreviewImage(null);
      }
    };
    fetchImage();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comboInput, selectedProductIdx, buyNowModalOpen]);

  useEffect(() => {
    if (!recommendedCombos.length) return;
    const currentSelProd = buyNowModal.products?.[selectedProductIdx] || null;
    if (!currentSelProd) return;
    const targetProduct = products?.find(p => p.ProductName === currentSelProd.productName);
    if (!targetProduct) return;

    let cancelled = false;
    Promise.all(
      recommendedCombos.map(async (combo) => {
        try {
          const variantsResult = await executeQuery(
            getVariantWithColourThemeAndPlatesByProductId,
            { ProductId: targetProduct.ProductId, CombinationType: combo, SalesChannel: 1, IsDisplayBrowse: false }
          );
          if (cancelled) return { combo, src: null };
          const variants = variantsResult?.getVariantByProductId;
          if (!variants?.length) return { combo, src: null };

          const colourName = currentSelProd.previewColourDisplayName;
          const variant = (colourName
            ? variants.find(v => v.ColourInfo?.DisplayName === colourName && v.SizeInfo?.SizeCode === 'ST')
              || variants.find(v => v.ColourInfo?.DisplayName === colourName)
            : null)
            || variants.find(v => v.SizeInfo?.SizeCode === 'ST')
            || variants[0];

          const input = preparePlateImageInput(variant, combo);
          if (!input) return { combo, src: null };
          const result = await executeMutation(plateImageGenerator, { input });
          return { combo, src: result?.generatePlateImage ? getPlateImageDataUrl(result.generatePlateImage) : null };
        } catch {
          return { combo, src: null };
        }
      })
    ).then((images) => {
      if (!cancelled) setRecommendationImages(images.filter(img => img.src));
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendedCombos, selectedProductIdx]);

  const mainRef  = useRef(null);
  const thumbRef = useRef(null);

  // Thumbnail ↔ main slider sync. We use react-splide's onMoved prop instead of
  // manual event wiring to avoid ref-timing and dependency-array issues.
  // Thumbnail taps are handled via React onClick on each SplideSlide (below) because
  // Splide's drag handler on mobile intercepts touch events before they become Splide events.
  const handleMainMoved = (_, idx) => thumbRef.current?.splide?.go(idx);
  const handleThumbMoved = (_, idx) => mainRef.current?.splide?.go(idx);

  // Splide reads heights during mount before the browser has finished computing
  // image dimensions. Refresh after a tick so heights are correct.
  useEffect(() => {
    if (!mainRef.current?.splide) return;
    const timer = setTimeout(() => {
      mainRef.current?.splide?.refresh();
    }, 0);
    return () => clearTimeout(timer);
  }, [galleryItems]);

  // Collapse the Specifications (furtherDetails) accordion when content flags it
  useEffect(() => {
    if (plateSpecs.specsCollapsedByDefault) {
      setOpenSections((prev) => ({ ...prev, furtherDetails: false }));
    }
  }, [plateSpecs.specsCollapsedByDefault]);

  useEffect(() => {
    if (!products || products.length === 0) dispatch(fetchAllBrowse());
  }, [dispatch, products]);

  const productSlug = pageData.productName
    ? pageData.productName.toLowerCase().replace(/\s+/g, '-')
    : style?.toLowerCase();
  const product = products?.find(
    (p) => p.ProductName?.toLowerCase().replace(/\s+/g, '-') === productSlug
  );

  const getUniqueColours = () => {
    if (!product?.VariantsInBrowseStyle) return [];
    const seen = new Set();
    return product.VariantsInBrowseStyle.filter((v) => {
      const name = v.ColourInfo?.Colour || v.Colour;
      if (!name || seen.has(name)) return false;
      seen.add(name);
      return true;
    });
  };

  const getSizeRows = () => {
    if (!product?.VariantsInBrowseStyle) return [];
    const seen = new Set();
    return product.VariantsInBrowseStyle
      .filter((v) => v.SizeInfo)
      .filter((v) => {
        const key = `${v.SizeInfo.SizeCode}_${v.SizeInfo.PlateWidth_mm}_${v.SizeInfo.PlateHeight_mm}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((v) => ({
        description: v.SizeInfo.SizeDescription || v.SizeInfo.SizeCode,
        width:   v.SizeInfo.PlateWidth_mm,
        height:  v.SizeInfo.PlateHeight_mm,
        slogans: Array.isArray(v.SizeInfo.SloganDesc)
          ? v.SizeInfo.SloganDesc.map((s) => s.SloganDescription || s.SloganText).filter(Boolean)
          : [],
      }));
  };

  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  if (loading || !pageLoaded || !products || products.length === 0) {
    return (
      <div id="content">
        <div className="row">
          <div className="products products--ready">
            <div className="products__inner">
              <ProductGridSkeleton />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product && !loading) {
    return <ErrorPage />;
  }

  if (!product) return (
    <div id="content">
      <div className="row">
        <div className="products products--ready">
          <div className="products__inner">
            <ProductGridSkeleton />
          </div>
        </div>
      </div>
    </div>
  );

  const modalProducts = buyNowModal.products || null;
  const selProd = modalProducts?.[selectedProductIdx] || null;
  const ruleStatuses = selProd
    ? (selProd.rules || []).map(rule => ({ ...rule, passes: evaluateRule(rule, comboInput) }))
    : [];
  const nonAltStatuses = ruleStatuses.filter(r => !r.isAlternative);
  const altStatuses = ruleStatuses.filter(r => r.isAlternative);
  const anyAltPasses = altStatuses.length === 0 || altStatuses.some(r => r.passes);
  const isComboValid = comboInput.length > 0 && nonAltStatuses.every(r => r.passes) && anyAltPasses;

  /* Use API colour variants when available; fall back to static colours from content JSON
     (e.g. Slimline Black has no API variants but production shows a "Black Tuxedo" swatch) */
  const apiColours    = getUniqueColours();
  const staticColours = (pageData.staticColours || []).map(sc => ({
    ColourInfo: { Colour: sc.name, ColourSelectorHexOuter: sc.bg, ColourSelectorHexFont: sc.fg },
  }));
  /* Swatches are purely visual, so de-duplicate on what the user actually sees
     (background + font hex). getUniqueColours() de-dupes on ColourInfo.Colour, but
     some products (e.g. DC Comics) return variants whose Colour codes differ while
     the swatch colours and DisplayName are identical — rendering duplicate swatches. */
    const swatchKey = (v) => {
    const name = (v.ColourInfo?.DisplayName || v.ColourInfo?.Colour || v.Colour || '').trim().toLowerCase();
    const bg = (v.ColourInfo?.ColourSelectorHexOuter || v.ColourSelectorHexOuter || '').replace(/^#/, '').toLowerCase();
    const fg = (v.ColourInfo?.ColourSelectorHexFont  || v.ColourSelectorHexFont  || '').replace(/^#/, '').toLowerCase();
    return `${name}|${bg}|${fg}`;
  };
  const dedupeSwatches = (list) => {
    const seen = new Set();
    return list.filter((v) => {
      const key = swatchKey(v);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };
  /* forceStaticColours: content JSON swatches replace API colours entirely
     (e.g. AFL Upgrade — API returns per-team variants, but the page shows a single White swatch) */
  const useStatic     = pageData.forceStaticColours && staticColours.length > 0;
  const colours       = dedupeSwatches(useStatic || apiColours.length === 0 ? staticColours : apiColours);
  const sizeRows      = getSizeRows();
  const uniqueSlogans = [...new Set(sizeRows.flatMap((s) => s.slogans))];
  const productTitle  = pageData.title || product.ProductName;
  const disclaimerText = overallDisclm.text || c.disclaimer || '';

  /* Colour swatches block — extracted so it can be positioned via coloursUnderPrice flag */
  const coloursBlock = colours.length > 0 && pageData.showAvailableColours !== false && (
    <div className={`product-summary__colours${colours.length <= COLOURS_VISIBLE ? ' insufficient-colours' : ''}`}>
      <h3>{c.availableColoursHeading || 'Available Colours'}</h3>
      <div className={`product-summary__colours-list-wrapper${coloursExpanded ? ' active' : ''}`}>
        <ul className="product-summary__colours-list" style={coloursExpanded ? { maxHeight: 'none' } : {}}>
          {(coloursExpanded ? colours : colours.slice(0, COLOURS_VISIBLE)).map((v, i) => {
            const bg = (v.ColourInfo?.ColourSelectorHexOuter || v.ColourSelectorHexOuter)?.replace(/^#/, '');
            const fg = (v.ColourInfo?.ColourSelectorHexFont || v.ColourSelectorHexFont)?.replace(/^#/, '');
            // Handle white backgrounds with inset border like production
            const hasInsetBorder = bg && bg.toLowerCase() === 'ffffff';
            return (
              <li
                key={i}
                className="product-summary__colours-list-item"
                title={v.ColourInfo?.DisplayName || v.ColourInfo?.Colour || v.Colour}
                style={{
                  backgroundColor: bg ? `#${bg}` : undefined,
                  color          : fg ? `#${fg}` : undefined,
                  boxShadow      : hasInsetBorder ? 'inset 0 0 0 1px #000' : 'none',
                }}
              >
                <span className="visuallyhidden">{v.ColourInfo?.DisplayName || v.ColourInfo?.Colour || v.Colour}</span>
              </li>
            );
          })}
        </ul>
        {colours.length > COLOURS_VISIBLE && (
          <div
            className="product-summary__colours-list-expand"
            onClick={() => setColoursExpanded(v => !v)}
            style={{ cursor: 'pointer' }}
          />
        )}
      </div>
      <div className="product-summary__colours-visibility">
        {c.showingPrefix || 'Showing'}{' '}
        <span className="product-summary__colours-visibility-shown">
          {coloursExpanded ? colours.length : Math.min(COLOURS_VISIBLE, colours.length)}
        </span>
        {' '}{c.showingOf || 'out of'}{' '}
        <span className="product-summary__colours-visibility-total">{colours.length}</span>
        {' '}{colours.length !== 1 ? (c.coloursWord || 'colours') : (c.colourWord || 'colour')}
      </div>
    </div>
  );

  return (
    <div id="content" className={styles.page}>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-header__content">
          <h1 className="page-header__title">{productTitle}</h1>
        </div>
      </div>

      {/* Product Header — gallery + price/CTA/colours */}
      <div className="product-header">
        <div className="component-content">

          {/* Subtitle — spans full width above the two columns */}
          {pageData.titleHtml && (
            <div
              className="product-header__subtitle"
              dangerouslySetInnerHTML={{ __html: pageData.titleHtml }}
            />
          )}

          {/* Gallery col */}
          <div className={`col-lg-9 product-header__slider ${styles.sliderCol}`}>
            {galleryItems.length > 0 ? (
                <div className={styles.splideWrapper}>
                  {/* Main slider */}
                  <Splide
                    ref={mainRef}
                    className={styles.mainSlider}
                    options={{
                      type      : 'loop',
                      autoHeight: true,
                      pagination: true,
                      arrows    : true,
                      cover     : false,
                      drag      : false,
                      ...(pageData.galleryStartIndex != null ? { start: pageData.galleryStartIndex } : {}),
                    }}
                    onMoved={galleryItems.length > 1 ? handleMainMoved : undefined}
                    aria-label={`${productTitle} plate images`}
                  >
                    {galleryItems.map((img, i) => (
                      <SplideSlide key={i}>
                        {img.videoUrl ? (
                          <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9' }}>
                            <iframe
                              src={img.videoUrl}
                              title={img.imageAltText || img.name}
                              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                              allowFullScreen
                              allow="autoplay; encrypted-media"
                            />
                          </div>
                        ) : (
                          <>
                            <img
                              src={img.imagePath}
                              alt={img.imageAltText || img.name}
                              width={img.width  || undefined}
                              height={img.height || undefined}
                              className={`${styles.splideImage}${img.allowZoom ? ` ${styles.zoomImage}` : ''}`}
                              {...(img.allowZoom ? {
                                onMouseMove: (e) => {
                                  const overlay = e.currentTarget.nextElementSibling;
                                  if (!overlay) return;
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  const x = ((e.clientX - rect.left) / rect.width) * 100;
                                  const y = ((e.clientY - rect.top) / rect.height) * 100;
                                  overlay.style.backgroundPosition = `${x}% ${y}%`;
                                }
                              } : {})}
                            />
                            {/* Desktop zoom overlay — large background-image pans with cursor on hover */}
                            {img.allowZoom && (
                              <div
                                className={styles.zoomOverlay}
                                style={{ backgroundImage: `url('${img.imagePath}')` }}
                              />
                            )}
                          </>
                        )}
                      </SplideSlide>
                    ))}
                  </Splide>

                  {/* Thumbnail nav – only when > 1 images */}
                  {galleryItems.length > 1 && (
                    <Splide
                      ref={thumbRef}
                      className={`thumbnail-slider ${styles.thumbSlider}`}
                      options={{
                        rewind      : true,
                        fixedWidth  : 138,
                        fixedHeight : 77,
                        isNavigation: true,
                        gap         : 29,
                        focus       : 'center',
                        trimSpace   : false,
                        pagination  : false,
                        arrows      : galleryItems.length > 1,
                        ...(pageData.galleryStartIndex != null ? { start: pageData.galleryStartIndex } : {}),
                      }}
                      onMoved={handleThumbMoved}
                      aria-label={`${productTitle} thumbnail navigation`}
                    >
                      {galleryItems.map((img, i) => (
                        <SplideSlide key={i} onClick={() => mainRef.current?.splide?.go(i)}>
                          <img
                            src={img.thumbnailPath || img.imagePath}
                            alt=""
                            aria-hidden="true"
                          />
                        </SplideSlide>
                      ))}
                    </Splide>
                  )}
                </div>
            ) : (
              <div className={styles.sliderPlaceholder} />
            )}
          </div>

          {/* Summary col */}
          <div className="col-lg-3 product-header__summary">
            <div className="product-summary">
              <div className="component-content">

                {/* Price */}
                <div className="product-summary__price">
                  <em>{pageData.priceFrom != null ? pageData.priceFrom : (c.pricePrefix || 'From')}</em>
                  <strong>{pageData.price
                    ? pageData.price
                    : `$${formatPrice(product.ListPrice)}`}</strong>
                </div>

                {/* Colour swatches — directly under price when coloursUnderPrice flag is set */}
                {pageData.coloursUnderPrice && coloursBlock}

                {/* Buy Now CTA — hidden when content sets hideBuyNow (e.g. auction-only products) */}
                {!pageData.hideBuyNow && (
                  <div className="product-summary__cta">
                    {buyNowModal.enabled ? (
                      <button
                        className="button button--slim-tablet-down"
                        onClick={() => setBuyNowModalOpen(true)}
                      >
                        {c.buyNowText || 'Buy Now'}
                      </button>
                    ) : (
                      <Link to="/create/check-combination" className="button button--slim-tablet-down">
                        {c.buyNowText || 'Buy Now'}
                      </Link>
                    )}
                  </div>
                )}

                {/* Colour swatches — default position (after CTA) when coloursUnderPrice is not set */}
                {!pageData.coloursUnderPrice && coloursBlock}

                {/* Sitecore description */}
                {pageData.description && (
                  <div
                    className="product-summary__description"
                    dangerouslySetInnerHTML={{ __html: pageData.description }}
                  />
                )}

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Product Information */}
      <div className="product-information">
        <div className="component-content">

          {/* Left col (9/12) — Good to know + Combination options */}
          <div className="col-lg-9">

            {/* Good to know */}
            {additionalInfo.details && (
              <div
                className={`product-information__col${openSections.additionalInfo ? ' active' : ''}`}
                onClick={() => toggleSection('additionalInfo')}
              >
                <h3 className="product-information__title">
                  {additionalInfo.title || 'Good to know'}
                </h3>
                <div className="product-information__expand-lvl1">
                  <div className="product-information__expand-lvl1-inner">
                    <div dangerouslySetInnerHTML={{ __html: additionalInfo.details }} />
                    {additionalInfo.expandedDetails &&
                      additionalInfo.expandedDetails.trim() !== '&nbsp;' && (
                        <div
                          className="product-information__expand-lvl2"
                          dangerouslySetInnerHTML={{ __html: additionalInfo.expandedDetails }}
                        />
                      )}
                  </div>
                </div>
                <div className="product-information__expand-toggle" />
              </div>
            )}

            {/* Combination options */}
            {(plateSpecs.combinationOptionsDescription || plateSpecs.combinationOptions) && (
              <div
                className={`product-information__col${openSections.combinations ? ' active' : ''}`}
                onClick={() => toggleSection('combinations')}
              >
                <h3 className="product-information__title">Combination options</h3>
                <div className="product-information__expand-lvl1">
                  <div className="product-information__expand-lvl1-inner">
                    <div dangerouslySetInnerHTML={{ __html: plateSpecs.combinationOptionsDescription }} />
                    {plateSpecs.combinationOptions && (
                      <div dangerouslySetInnerHTML={{ __html: plateSpecs.combinationOptions }} />
                    )}
                  </div>
                </div>
                <div className="product-information__expand-toggle" />
              </div>
            )}

          </div>

          {/* Right col (3/12) — Further Details */}
          <div className="col-lg-3">
            <div
              className={`product-information__col no-lvl2${openSections.furtherDetails ? ' active' : ''}`}
              onClick={() => toggleSection('furtherDetails')}
            >
              <h3 className="product-information__title">
                {plateSpecs.title || c.furtherDetailsTitle || 'Further Details'}
              </h3>
              <div className="product-information__expand-lvl1">
                <div className="product-information__expand-lvl1-inner">

                  {/* Price */}
                  {plateSpecs.priceDetails ? (
                    <>
                      <h4>{c.priceLabel || 'Price'}</h4>
                      <div dangerouslySetInnerHTML={{ __html: plateSpecs.priceDetails }} />
                    </>
                  ) : (
                    <>
                      <h4>{c.priceLabel || 'Price'}</h4>
                      <p>{c.pricePrefix || 'From'} ${formatPrice(product.ListPrice)}</p>
                    </>
                  )}

                  {/* Size */}
                  {plateSpecs.size ? (
                    <>
                      <h4>{c.sizeLabel || 'Size'}</h4>
                      <div dangerouslySetInnerHTML={{ __html: plateSpecs.size }} />
                    </>
                  ) : sizeRows.length > 0 && (
                    <>
                      <h4>{c.sizeLabel || 'Size'}</h4>
                      {sizeRows.map((s, i) => (
                        <p key={i}>
                          {s.description}
                          {s.width && s.height
                            ? ` — ${s.width}mm (w) × ${s.height}mm (h)`
                            : ''}
                        </p>
                      ))}
                    </>
                  )}

                  {/* Slogan */}
                  {plateSpecs.slogan ? (
                    <>
                      <h4>{c.sloganLabel || 'Slogan'}</h4>
                      <div dangerouslySetInnerHTML={{ __html: plateSpecs.slogan }} />
                    </>
                  ) : uniqueSlogans.length > 0 && (
                    <>
                      <h4>{c.sloganLabel || 'Slogan'}</h4>
                      {uniqueSlogans.map((slogan, i) => <p key={i}>{slogan}</p>)}
                    </>
                  )}

                  {!plateSpecs.hideAdditionalInfo1 && plateSpecs.additionalInfo1 && (
                    <div dangerouslySetInnerHTML={{ __html: plateSpecs.additionalInfo1 }} />
                  )}
                  {!plateSpecs.hideAdditionalInfo2 && plateSpecs.additionalInfo2 && (
                    <div dangerouslySetInnerHTML={{ __html: plateSpecs.additionalInfo2 }} />
                  )}

                </div>
              </div>
              <div className="product-information__expand-toggle" />
            </div>
          </div>

        </div>
      </div>

      {/* Secondary product sections (e.g. Euro Premium on the Euro page) */}
      {secondaryProduct && (
        <>
          {/* Secondary product: description + specs */}
          <div className="product-information">
            <div className="component-content">
              <div className="col-lg-9">
                {secondaryProduct.additionalDetails?.details && (
                  <div
                    className={`product-information__col${openSections.secondaryDetails ? ' active' : ''}`}
                    onClick={() => toggleSection('secondaryDetails')}
                  >
                    <h3 className="product-information__title">
                      {secondaryProduct.additionalDetails.title}
                    </h3>
                    <div className="product-information__expand-lvl1">
                      <div className="product-information__expand-lvl1-inner">
                        <div dangerouslySetInnerHTML={{ __html: secondaryProduct.additionalDetails.details }} />
                      </div>
                    </div>
                    <div className="product-information__expand-toggle" />
                  </div>
                )}
              </div>
              <div className="col-lg-3">
                {secondaryProduct.plateSpecs && (
                  <div
                    className={`product-information__col no-lvl2${openSections.secondarySpecs ? ' active' : ''}`}
                    onClick={() => toggleSection('secondarySpecs')}
                  >
                    <h3 className="product-information__title">
                      {secondaryProduct.plateSpecs.title || c.furtherDetailsTitle || 'Further Details'}
                    </h3>
                    <div className="product-information__expand-lvl1">
                      <div className="product-information__expand-lvl1-inner">
                        {secondaryProduct.plateSpecs.priceDetails && (
                          <>
                            <h4>{c.priceLabel || 'Price'}</h4>
                            <div dangerouslySetInnerHTML={{ __html: secondaryProduct.plateSpecs.priceDetails }} />
                          </>
                        )}
                        {secondaryProduct.plateSpecs.size && (
                          <>
                            <h4>{c.sizeLabel || 'Size'}</h4>
                            <div dangerouslySetInnerHTML={{ __html: secondaryProduct.plateSpecs.size }} />
                          </>
                        )}
                        {secondaryProduct.plateSpecs.slogan && (
                          <>
                            <h4>{c.sloganLabel || 'Slogan'}</h4>
                            <div dangerouslySetInnerHTML={{ __html: secondaryProduct.plateSpecs.slogan }} />
                          </>
                        )}
                      </div>
                    </div>
                    <div className="product-information__expand-toggle" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Secondary product: combination options + good to know */}
          {(secondaryProduct.combinationOptionsDescription || secondaryProduct.goodToKnow) && (
            <div className="product-information">
              <div className="component-content">
                <div className="col-lg-9">
                  {secondaryProduct.combinationOptionsDescription && (
                    <div
                      className={`product-information__col${openSections.secondaryCombinations ? ' active' : ''}`}
                      onClick={() => toggleSection('secondaryCombinations')}
                    >
                      <h3 className="product-information__title">
                        {secondaryProduct.combinationOptionsTitle || 'Combination options'}
                      </h3>
                      <div className="product-information__expand-lvl1">
                        <div className="product-information__expand-lvl1-inner">
                          <div dangerouslySetInnerHTML={{ __html: secondaryProduct.combinationOptionsDescription }} />
                          {secondaryProduct.combinationOptions && (
                            <div dangerouslySetInnerHTML={{ __html: secondaryProduct.combinationOptions }} />
                          )}
                        </div>
                      </div>
                      <div className="product-information__expand-toggle" />
                    </div>
                  )}
                  {secondaryProduct.goodToKnow?.details && (
                    <div
                      className={`product-information__col${openSections.secondaryGoodToKnow ? ' active' : ''}`}
                      onClick={() => toggleSection('secondaryGoodToKnow')}
                    >
                      <h3 className="product-information__title">
                        {secondaryProduct.goodToKnow.title || 'Good to know'}
                      </h3>
                      <div className="product-information__expand-lvl1">
                        <div className="product-information__expand-lvl1-inner">
                          <div dangerouslySetInnerHTML={{ __html: secondaryProduct.goodToKnow.details }} />
                        </div>
                      </div>
                      <div className="product-information__expand-toggle" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Content holder — disclaimers (matches production content-holder section) */}
      <div className="content-holder">
        {pageData.imageDisclaimer && (
          <div className="product-detail__text-disclaimer"
               dangerouslySetInnerHTML={{ __html: pageData.imageDisclaimer }} />
        )}
        {overallDisclm.html ? (
          <div className={styles.overallDisclaimer}
               dangerouslySetInnerHTML={{ __html: overallDisclm.html }} />
        ) : disclaimerText ? (
          <p className={styles.overallDisclaimer}>{disclaimerText}</p>
        ) : null}
      </div>

      {buyNowModal.enabled && (
        <>
          <style>{`
            @media (min-width: 660px) {
              .modal-selection {
                transition: translate 0.5s ease-in-out, overlay 0.5s allow-discrete, display 0.5s allow-discrete;
                translate: 100% 0;
              }
              .modal-selection[open] { translate: 0 0; }
              @starting-style {
                .modal-selection[open] { translate: 100% 0; }
              }
              .modal-product-combo {
                transition: translate 0.5s ease-in-out, overlay 0.5s allow-discrete, display 0.5s allow-discrete;
                translate: 100% 0;
              }
              .modal-product-combo[open] { translate: 0 0; }
              @starting-style {
                .modal-product-combo[open] { translate: 100% 0; }
              }
            }
          `}</style>

          {modalProducts ? (
            <dialog
              ref={buyNowDialogRef}
              className="modal-product-combo"
              onClose={() => { setBuyNowModalOpen(false); setComboInput(''); setSelectedProductIdx(0); }}
              onClick={(e) => { if (e.target === buyNowDialogRef.current) buyNowDialogRef.current.close(); }}
            >
              <div className="modal-product-combo__inner">
                <div className="modal-product-combo__inner-back" onClick={() => buyNowDialogRef.current.close()} />
                <div className="modal-product-combo__content">

                  <div className="modal-product-combo__header">
                    <button type="button" className="modal-product-combo__close" onClick={() => buyNowDialogRef.current.close()}>
                      <span className="visuallyhidden">Close</span>
                    </button>
                    <h3 className="modal-product-combo__title" tabIndex={-1}>{productTitle}</h3>
                  </div>

                  <div className="modal-product-combo__body">

                    {modalProducts.length > 1 && (
                      <fieldset form="product-combo-form" className="modal-product-combo__product-selection">
                        {modalProducts.map((mp, i) => (
                          <div key={i} className="modal-product-combo__product-selection-option">
                            <input
                              type="radio"
                              id={`config-option-${i}`}
                              name="config-option"
                              form="product-combo-form"
                              className="modal-product-combo__product-selection-input visuallyhidden visuallyhidden--no-focus"
                              value={i}
                              checked={selectedProductIdx === i}
                              onChange={() => setSelectedProductIdx(i)}
                            />
                            <label htmlFor={`config-option-${i}`} className="modal-product-combo__product-selection-label">
                              {mp.label}
                            </label>
                          </div>
                        ))}
                      </fieldset>
                    )}

                    {(generatedPreviewImage || selProd?.previewImagePath) && (
                      <img
                        width="520"
                        height="140"
                        alt={`Preview image of a ${selProd?.label} plate`}
                        src={generatedPreviewImage || selProd.previewImagePath}
                        className="modal-product-combo__preview"
                      />
                    )}

                    {selProd?.disclaimer && (
                      <div className="modal-product-combo__disclaimer" dangerouslySetInnerHTML={{ __html: selProd.disclaimer }} />
                    )}

                    <form
                      autoComplete="off"
                      id="product-combo-form"
                      className="modal-product-combo__form"
                      onSubmit={async (e) => {
                        e.preventDefault();
                        if (!isComboValid || checkingAvailability) return;
                        const fullCombo = (selProd?.comboPrefix || '') + comboInput;
                        setCheckingAvailability(true);
                        setAvailabilityError('');
                        try {
                          const result = await executeQuery(validatePlateNumber, {
                            PlateCombo: fullCombo,
                            VehicleType: selProd?.vehicleType ?? 1,
                            SalesChannel: 1,
                          });
                          if (result?.getPlateRuleEngineStatus?.Status !== 1) {
                            setAvailabilityError(c.availabilityErrorText || 'This combo is not available');
                            setIsAvailable(false);
                            setRecommendedCombos([]);
                            setRecommendationImages([]);
                            setRecsLoading(true);
                            executeMutation(combinationRecommender, {
                              input: {
                                plateNumber: fullCombo,
                                groupName: selProd?.recommenderGroupName || '',
                                vehicleType: (selProd?.vehicleType ?? 1) === 1 ? 'Car' : 'Motorbike',
                                maxNumberOfRecommendations: 6
                              }
                            })
                              .then((recResult) => {
                                const body = recResult?.recommender?.body;
                                if (!body) return;
                                const parsed = typeof body === 'string' ? JSON.parse(body) : body;
                                const recs = parsed.recommendations || parsed.data || parsed || [];
                                setRecommendedCombos(recs.slice(0, 6).map(r => r.PlateCombo || r.combo || r));
                              })
                              .catch(() => {})
                              .finally(() => setRecsLoading(false));
                            return;
                          }
                          setIsAvailable(true);
                        } catch {
                          setAvailabilityError(c.availabilityVerifyErrorText || 'Unable to verify availability. Please try again.');
                        } finally {
                          setCheckingAvailability(false);
                        }
                      }}
                    >
                      <div className="modal-product-combo__form-body">
                        <label htmlFor="modal-product-combo-input" className="modal-product-combo__label">
                          {c.enterCombinationLabel || 'Enter a combination'}
                        </label>

                        {selProd?.patternsHtml && (
                          <details className="modal-product-combo__patterns">
                            <summary className="modal-product-combo__patterns-title">
                              {c.patternsAllowedLabel || 'Patterns allowed'}
                            </summary>
                            <div
                              className="modal-product-combo__patterns-content"
                              dangerouslySetInnerHTML={{ __html: selProd.patternsHtml }}
                            />
                          </details>
                        )}

                        <div className={`modal-product-combo__box${isAvailable === true ? ' modal-product-combo__box--success' : isAvailable === false ? ' modal-product-combo__box--failed' : ''}`}>
                          {selProd?.comboPrefix && <div className="modal-product-combo__prefix">{selProd.comboPrefix}</div>}
                          <input
                            placeholder={selProd?.inputPlaceholder || ''}
                            maxLength={selProd?.inputMaxLength || 10}
                            id="modal-product-combo-input"
                            name="modal-product-combo-input"
                            required
                            className="modal-product-combo__input"
                            value={comboInput}
                            onChange={(e) => setComboInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                          />
                        </div>

                        <output name="combination-validity" htmlFor="modal-product-combo-input">
                          {isAvailable === true ? (
                            <div className="modal-product-combo__success-message" role="status">
                              {c.availableText || 'This combo is available!'}
                            </div>
                          ) : isAvailable === null ? (
                            <>
                              <h4 className="visuallyhidden">
                                {c.rulesHeadingPrefix || 'Rules for'} {selProd?.label || productTitle} {c.rulesHeadingSuffix || 'plates'}
                              </h4>
                              <ul className="modal-product-combo__rule-list">
                                {ruleStatuses.map((rule, i) => {
                                  if (rule.onlyDisplayUponFail && rule.passes) return null;
                                  const showAsFail = rule.isAlternative
                                    ? (comboInput.length > 0 && !anyAltPasses)
                                    : (comboInput.length > 0 || rule.onlyDisplayUponFail);
                                  return (
                                    <li key={i} className={`modal-product-combo__rule${rule.passes ? ' modal-product-combo__rule--success' : showAsFail ? ' modal-product-combo__rule--fail' : ''}`}>
                                      <div className="modal-product-combo__rule-inner">
                                        <div className="modal-product-combo__rule-message" dangerouslySetInnerHTML={{ __html: rule.message }} />
                                        {rule.includeCharacterCount && (
                                          <p className="modal-product-combo__count">
                                            {comboInput.length}/{selProd?.inputMaxLength || 0}
                                            <span className="visuallyhidden"> characters entered</span>
                                          </p>
                                        )}
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            </>
                          ) : null}
                        </output>
                      </div>

                      {isAvailable === false && availabilityError && (
                        <div className="modal-product-combo__failed-message" role="alert">{availabilityError}</div>
                      )}

                      <div className="modal-product-combo__button-list">
                        {isAvailable === true ? (
                          <>
                            <button
                              type="button"
                              className="modal-product-combo__button"
                              onClick={() => {
                                const fullCombo = (selProd?.comboPrefix || '') + comboInput;
                                const vt = (selProd?.vehicleType ?? 1) === 1 ? 'car' : 'motorcycle';
                                const colourParam = selProd?.previewColourDisplayName || '';
                                navigate(`/create/select-a-style?combination=${encodeURIComponent(fullCombo)}&vehicleType=${vt}&productName=${encodeURIComponent(selProd?.productName || '')}${colourParam ? `&colour=${encodeURIComponent(colourParam)}` : ''}`);
                              }}
                            >
                              <span>{c.applyAndContinueText || 'Apply and continue'}</span>
                            </button>
                            <button
                              type="button"
                              className="modal-product-combo__button modal-product-combo__button--reset"
                              onClick={() => {
                                setComboInput('');
                                setIsAvailable(null);
                                setAvailabilityError('');
                                setGeneratedPreviewImage(null);
                                setRecommendedCombos([]);
                                setRecommendationImages([]);
                              }}
                            >
                              <span>{c.resetComboText || 'Reset combo'}</span>
                            </button>
                          </>
                        ) : isAvailable === false ? (
                          <button
                            type="button"
                            className="modal-product-combo__button"
                            onClick={() => {
                              setComboInput('');
                              setIsAvailable(null);
                              setAvailabilityError('');
                              setGeneratedPreviewImage(null);
                              setRecommendedCombos([]);
                              setRecommendationImages([]);
                            }}
                          >
                            <span>{c.tryAnotherComboText || 'Try another combo'}</span>
                          </button>
                        ) : (
                          <button
                            type="submit"
                            disabled={!isComboValid || checkingAvailability}
                            className={`modal-product-combo__button${(!isComboValid || checkingAvailability) ? ' modal-product-combo__button--disabled' : ''}`}
                          >
                            <span>{checkingAvailability ? (c.checkingText || 'Checking…') : (c.checkAvailabilityText || 'Check availability')}</span>
                          </button>
                        )}
                      </div>
                    </form>

                    {isAvailable === false && (recsLoading || recommendationImages.length > 0) && (
                      <div className="modal-product-combo__recommendations">
                        <h3 className="modal-product-combo__recommendations-title">
                          {c.similarCombosTitle || 'Similar combinations available'}
                        </h3>
                        {recsLoading ? (
                          <p className="modal-product-combo__recommendations-loading">
                            {c.recsLoadingText || 'Looking for similar combinations\u2026'}
                          </p>
                        ) : (
                          <ul className="modal-product-combo__recommendations-list">
                            {recommendationImages.map(({ combo, src }) => (
                              <li key={combo} className="modal-product-combo__recommendations-item">
                                <a
                                  href={`/create/select-a-style?combination=${encodeURIComponent(combo)}&vehicleType=${(selProd?.vehicleType ?? 1) === 1 ? 'car' : 'motorcycle'}&productName=${encodeURIComponent(selProd?.productName || '')}`}
                                  className="modal-product-combo__recommendations-link"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    const vt = (selProd?.vehicleType ?? 1) === 1 ? 'car' : 'motorcycle';
                                    const colourParam = selProd?.previewColourDisplayName || '';
                                    navigate(`/create/select-a-style?combination=${encodeURIComponent(combo)}&vehicleType=${vt}&productName=${encodeURIComponent(selProd?.productName || '')}${colourParam ? `&colour=${encodeURIComponent(colourParam)}` : ''}`);
                                    buyNowDialogRef.current.close();
                                  }}
                                >
                                  <img
                                    src={src}
                                    alt={`Preview of plate with combination ${combo}`}
                                    width="520"
                                    height="140"
                                    className="modal-product-combo__recommendations-image"
                                  />
                                </a>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}

                  </div>
                </div>
              </div>
            </dialog>
          ) : (
            <dialog
              ref={buyNowDialogRef}
              className="modal-selection"
              onClose={() => setBuyNowModalOpen(false)}
              onClick={(e) => {
                if (e.target === buyNowDialogRef.current) buyNowDialogRef.current.close();
              }}
            >
              <div className="modal-selection__inner">
                <div
                  className="modal-selection__inner-back"
                  onClick={() => buyNowDialogRef.current.close()}
                />
                <div className="modal-selection__content">

                  <div className="modal-selection__header">
                    <button
                      type="button"
                      className="modal-selection__close"
                      onClick={() => buyNowDialogRef.current.close()}
                    >
                      <span className="visuallyhidden">Close</span>
                    </button>
                    <h3 className="modal-selection__title" tabIndex={-1}>{productTitle}</h3>
                  </div>

                  <div className="modal-selection__body">
                    {galleryItems.length > 0 && (
                      <div className="modal-style-form__preview">
                        <dl className="modal-style-form__preview-list">
                          <div className="modal-style-form__preview-list-item">
                            <dd className="modal-style-form__preview-list-img-container">
                              <img
                                className="modal-style-form__preview-list-img"
                                src={galleryItems[0].imagePath}
                                alt={galleryItems[0].imageAltText || productTitle}
                                width="520"
                                height="140"
                              />
                            </dd>
                          </div>
                        </dl>
                      </div>
                    )}
                    {(plateSpecs.combinationOptionsDescription || plateSpecs.combinationOptions) && (
                      <div className="modal-style-form__field">
                        <h4 className="modal-style-form__label">
                          {c.buyNowModalCombinationTitle || 'Combination options'}
                        </h4>
                        {plateSpecs.combinationOptionsDescription && (
                          <div dangerouslySetInnerHTML={{ __html: plateSpecs.combinationOptionsDescription }} />
                        )}
                        {plateSpecs.combinationOptions && (
                          <div dangerouslySetInnerHTML={{ __html: plateSpecs.combinationOptions }} />
                        )}
                      </div>
                    )}
                  </div>

                  <div className="modal-selection__footer-trigger" />
                  <div className="modal-selection__footer">
                    <button
                      className="modal-selection__footer-cta"
                      onClick={() => navigate('/create/check-combination')}
                    >
                      {c.checkAvailabilityText || 'Check availability'}
                    </button>
                  </div>

                </div>
              </div>
            </dialog>
          )}
        </>
      )}

    </div>
  );
};

export default BrowseStyleDetailPage;
